import { useEffect, useState } from 'react';
import { SCROLL_PINNED_ATTRIBUTE } from './scroll-indicator';

/**
 * How far the overlays reach under the bar. The bar paints above them, so a
 * pixel of overlap costs nothing and closes the hairline sub-pixel layout
 * would otherwise leave between the fade and the bar.
 */
const TUCK_UNDER_PX = 1;

/**
 * The bar's distance above the wrapper's bottom, in layout pixels.
 *
 * Read from screen rects, which a transform scales: the dialog opens with a
 * zoom, and a reading taken mid-animation would otherwise be off by the zoom
 * until something happened to re-measure. Dividing by the wrapper's on-screen
 * scale gives the same answer at any point of the animation.
 */
export function pinnedInsetOf(container: Element, pinned: Element): number {
    const box = container.getBoundingClientRect();
    const layoutHeight = (container as HTMLElement).offsetHeight;
    const scale =
        layoutHeight > 0 && box.height > 0 ? box.height / layoutHeight : 1;
    const inset = (box.bottom - pinned.getBoundingClientRect().top) / scale;
    return Math.max(0, Math.floor(inset) - TUCK_UNDER_PX);
}

/**
 * How far a bar pinned inside the scroller sits above the bottom of the
 * scroller's wrapper, in pixels, or `null` when the step pins nothing.
 *
 * The overlays that mark the fold (the fade, the hint) are positioned against
 * that wrapper. A fixed offset guessed from the bar's usual height leaves a
 * strip of content showing between the overlay and the bar whenever the bar
 * runs taller or shorter than the guess: a wrapped label, a second button, the
 * device safe area on the sheet. Measuring puts the overlay on the bar's edge.
 */
export const usePinnedInset = (scrollEl: HTMLElement | null) => {
    const [inset, setInset] = useState<number | null>(null);

    useEffect(() => {
        const container = scrollEl?.parentElement;
        if (!scrollEl || !container) return;
        const measure = () => {
            const pinned = scrollEl.querySelector(
                `[${SCROLL_PINNED_ATTRIBUTE}]`,
            );
            if (!pinned) {
                setInset(null);
                return;
            }
            setInset(pinnedInsetOf(container, pinned));
        };
        const resize =
            typeof ResizeObserver === 'undefined'
                ? null
                : new ResizeObserver(measure);
        const observeAll = () => {
            if (!resize) return;
            resize.disconnect();
            resize.observe(scrollEl);
            const pinned = scrollEl.querySelector(
                `[${SCROLL_PINNED_ATTRIBUTE}]`,
            );
            if (pinned) resize.observe(pinned);
        };
        // A step swap brings its own bar, or none.
        const mutations = new MutationObserver(() => {
            observeAll();
            measure();
        });
        mutations.observe(scrollEl, { childList: true, subtree: true });
        // A sticky bar only moves with the scroll before it sticks; the same
        // value on every other frame, which React bails out of re-rendering.
        scrollEl.addEventListener('scroll', measure, { passive: true });
        observeAll();
        measure();
        return () => {
            resize?.disconnect();
            mutations.disconnect();
            scrollEl.removeEventListener('scroll', measure);
        };
    }, [scrollEl]);

    return inset;
};
